// 2P AI — scheda Notifiche (§8.3, R5). Due canali: push ad app chiusa (qui si attiva) + lista in-app col badge.
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { enablePush, pushStatus, type PushStatus } from '../lib/push';
import { Icon } from '../components/Icon';

interface NItem {
  id: string; event_type: string; tier: string; session_id: string | null;
  title: string; body: string; url: string | null; read_at: string | null; created_at: string;
}
interface NList { items: NItem[]; unread: number }

const ICON: Record<string, string> = {
  approval_request: 'pan_tool', session_done: 'check_circle', session_error: 'error',
  session_timeout: 'schedule', server_problem: 'dns',
};

export function NotificationsTab() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { void pushStatus().then(setStatus); }, []);

  const list = useQuery<NList>({ queryKey: ['notifications'], queryFn: () => api.get<NList>('/notifications'), refetchInterval: 20000 });
  const read = useMutation({
    mutationFn: (id?: string) => api.post('/notifications/read', id ? { id } : {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  async function activate() {
    setErr(null); setBusy(true);
    try { setStatus(await enablePush()); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Errore'); }
    finally { setBusy(false); }
  }

  function openItem(n: NItem) {
    if (n.read_at === null) read.mutate(n.id);
    if (n.url) navigate(n.url);
  }

  const items = list.data?.items ?? [];

  return (
    <>
      <div className="scr-head">
        <div>
          <div className="scr-title">Notifiche</div>
          <div className="scr-sub">Ti avvisano sul telefono anche ad app chiusa</div>
        </div>
        {(list.data?.unread ?? 0) > 0 ? (
          <button className="btn-ghost-sm" type="button" onClick={() => read.mutate(undefined)}>Segna tutte lette</button>
        ) : null}
      </div>

      {/* Attivazione push */}
      <section className="nlist" aria-label="Attivazione notifiche">
        {status === 'subscribed' ? (
          <div className="nrow static"><div className="ic ok"><Icon name="notifications_active" /></div>
            <div className="body"><div className="ttl">Notifiche attive su questo dispositivo</div>
              <div className="desc">Riceverai un avviso quando una sessione attende il tuo OK, finisce o va in errore.</div></div></div>
        ) : status === 'need-home' ? (
          <div className="nrow static"><div className="ic app"><Icon name="add_to_home_screen" /></div>
            <div className="body"><div className="ttl">Aggiungi l'app alla schermata Home</div>
              <div className="desc">Su iPhone: Condividi → "Aggiungi alla Home", poi apri l'app dall'icona e torna qui per attivare le notifiche.</div></div></div>
        ) : status === 'denied' ? (
          <div className="nrow static"><div className="ic"><Icon name="notifications_off" /></div>
            <div className="body"><div className="ttl">Permesso negato</div>
              <div className="desc">Abilitalo da Impostazioni iOS → Notifiche → 2P AI, poi riprova.</div></div></div>
        ) : status === 'unsupported' ? (
          <div className="nrow static"><div className="ic"><Icon name="notifications_off" /></div>
            <div className="body"><div className="ttl">Notifiche non supportate qui</div>
              <div className="desc">Serve iOS 16.4+ con l'app aggiunta alla Home, o un browser desktop compatibile.</div></div></div>
        ) : (
          <div className="nrow static"><div className="ic app"><Icon name="notifications" /></div>
            <div className="body"><div className="ttl">Attiva le notifiche</div>
              <div className="desc">{status === 'granted' ? 'Completa l\'iscrizione per ricevere i push.' : 'Ti chiederemo il permesso del telefono.'}</div></div>
            <button className="btn-primary" type="button" disabled={busy} onClick={() => void activate()}>{busy ? '…' : 'Attiva'}</button></div>
        )}
        {err ? <div className="note danger"><Icon name="error" /><span>{err}</span></div> : null}
      </section>

      <div className="subhead">Storico</div>
      <section className="nlist" aria-label="Storico notifiche">
        {items.length === 0 ? (
          <div className="nrow static"><div className="ic ok"><Icon name="notifications_off" /></div>
            <div className="body"><div className="ttl">Nessuna notifica</div>
              <div className="desc">Le richieste di OK, gli esiti delle sessioni e le allerte server compariranno qui.</div></div></div>
        ) : items.map((n) => (
          <button key={n.id} type="button" className={`nrow${n.read_at === null ? ' unread' : ''}`} onClick={() => openItem(n)}>
            <div className={`ic ${n.event_type === 'session_error' || n.event_type === 'server_problem' ? 'err' : n.event_type === 'approval_request' ? 'app' : 'ok'}`}>
              <Icon name={ICON[n.event_type] ?? 'notifications'} />
            </div>
            <div className="body"><div className="ttl">{n.title}</div><div className="desc">{n.body}</div></div>
            {n.read_at === null ? <span className="dot" aria-label="non letta" /> : null}
          </button>
        ))}
      </section>
    </>
  );
}
